import type { LandingContext } from "./types";
import { loadFormConfig } from "./config";
import { loadSlotRegistry } from "./slots";
import { pickVariant } from "./variant";
import { armTrigger } from "./triggers";
import { renderInline, renderPopup } from "./render";
import { trackImpression, trackPageView } from "./beacon";
import { landingContextOf, trackPageButtons } from "./landing";

declare global {
  interface Window {
    __hrnFormsInit?: Set<HTMLScriptElement>;
    __hrnFormsPopups?: Set<string>;
    __hrnFormsPages?: Set<string>;
  }
}

interface Placement {
  formId?: string;
  slot?: string;
  /** Where an inline form renders. Popups ignore it. */
  target: Element | null;
  targetLabel: string;
  /** The marker sits on one of the form's landing pages. */
  landing?: LandingContext;
}

async function resolveFormId(origin: string, placement: Placement): Promise<string | null> {
  if (placement.formId) return placement.formId;
  if (!placement.slot) return null;
  const registry = await loadSlotRegistry(origin);
  return registry[placement.slot] ?? null;
}

interface ScriptOptions {
  /** data-api: overrides the published config's apiBase — local development only. */
  apiOverride?: string;
  /** data-preview-rev: load that publish's uncached preview copy (digits only). */
  previewRev?: string;
  /** data-preview-state="success": render the thank-you state without a sign-up. */
  previewSuccess?: boolean;
}

async function mount(origin: string, options: ScriptOptions, placement: Placement): Promise<void> {
  const formId = await resolveFormId(origin, placement).catch((err) => {
    console.error(err);
    return null;
  });

  // A slot with nothing currently assigned to it — this is a normal, silent no-op,
  // not an error: the tag stays on the page, waiting for the registry to point it
  // at a form later.
  if (!formId) return;

  try {
    const config = await loadFormConfig(origin, formId, options.previewRev);
    // Deactivated in the admin — same silent no-op as an unassigned slot.
    if (config.active === false) return;

    const apiBase = (options.apiOverride ?? config.apiBase).replace(/\/+$/, "");
    const variant = pickVariant(config);

    // A landing page exists only for its form: always inline, whatever trigger the
    // form has elsewhere, never suppressed, and a page view counted alongside the
    // impression. A page may show the form in several places; the view, and the
    // page's button tracking, are set up once per page load.
    // A page preview (the uncached copy a save returns) counts nothing.
    if (placement.landing && placement.target) {
      const landing = placement.landing;
      renderInline(apiBase, config, variant, placement.target, { ...options, landing });
      if (!options.previewSuccess && !landing.preview) {
        trackImpression(apiBase, formId, variant.id);
        window.__hrnFormsPages ??= new Set();
        if (!window.__hrnFormsPages.has(landing.pageId)) {
          window.__hrnFormsPages.add(landing.pageId);
          trackPageView(apiBase, landing.pageId, landing.pageVariantId ?? "");
          trackPageButtons(apiBase, landing);
        }
      }
      return;
    }

    if (config.trigger.type === "inline") {
      if (!placement.target) {
        console.error(`hrn-forms: inline target "${placement.targetLabel}" not found`);
        return;
      }
      renderInline(apiBase, config, variant, placement.target, options);
      if (!options.previewSuccess) trackImpression(apiBase, formId, variant.id);
      return;
    }

    // Several placements can resolve to the same popup form; arm it once.
    window.__hrnFormsPopups ??= new Set();
    if (window.__hrnFormsPopups.has(formId)) return;
    window.__hrnFormsPopups.add(formId);

    // A success-state preview shows the popup right away rather than waiting for
    // its trigger, and counts nothing.
    if (options.previewSuccess) {
      renderPopup(apiBase, config, variant, options);
      return;
    }

    armTrigger(formId, config.trigger, () => {
      renderPopup(apiBase, config, variant);
      trackImpression(apiBase, formId, variant.id);
    });
  } catch (err) {
    console.error(err);
  }
}

// Scan mode: one bare script tag on the page, and a marker element wherever a
// form may appear — <div data-hrn-forms-slot="..."> or <div data-hrn-forms-form="...">.
function scan(origin: string, options: ScriptOptions): void {
  const markers = document.querySelectorAll<HTMLElement>(
    "[data-hrn-forms-slot]:not([data-hrn-forms-mounted]), [data-hrn-forms-form]:not([data-hrn-forms-mounted])"
  );
  markers.forEach((el) => {
    el.setAttribute("data-hrn-forms-mounted", "");
    mount(origin, options, {
      formId: el.dataset.hrnFormsForm,
      slot: el.dataset.hrnFormsSlot,
      target: el,
      targetLabel: "(marker element)",
      landing: landingContextOf(el),
    });
  });
}

function init(scriptEl: HTMLScriptElement): void {
  window.__hrnFormsInit ??= new Set();
  if (window.__hrnFormsInit.has(scriptEl)) return;
  window.__hrnFormsInit.add(scriptEl);

  const origin = new URL(scriptEl.src, location.href).origin;
  const previewRev = scriptEl.dataset.previewRev;
  const options: ScriptOptions = {
    apiOverride: scriptEl.dataset.api,
    previewRev: previewRev && /^\d+$/.test(previewRev) ? previewRev : undefined,
    previewSuccess: scriptEl.dataset.previewState === "success",
  };
  const formId = scriptEl.dataset.form;
  const slot = scriptEl.dataset.slot;

  if (!formId && !slot) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => scan(origin, options));
    } else {
      scan(origin, options);
    }
    return;
  }

  const targetSelector = scriptEl.dataset.target;
  mount(origin, options, {
    formId,
    slot,
    target: targetSelector ? document.querySelector(targetSelector) : scriptEl.parentElement,
    targetLabel: targetSelector ?? "(script parent)",
  });
}

// Support multiple embed tags on one page: init runs once per script element,
// via document.currentScript at execution time (works for both classic and defer scripts).
const current = document.currentScript as HTMLScriptElement | null;
if (current) init(current);
